import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import SearchField from 'src/shared/components/form/searchField';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import { EventStatusTabKeysEnum } from 'src/shared/models/enums/eventStatus.enum';
import dayjs from 'dayjs';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { FormProvider, useForm } from 'react-hook-form';
import { ControlledMultiDistrictPicker } from 'src/shared/components/form/controlledMultiDistrictPicker';
import { District } from 'src/shared/models/district/district';
import { EventDateFilter, EventSearchFilter, EventSearchState } from '../models/eventSearchFilter';
import EventsDataGrid from '../components/eventsDataGrid';
import { EventOverviewStatuses } from '../components/eventOverviewStatuses';
import { EventStatusTabItem } from '../components/eventStatusTabs';

export const EventListPage = () => {
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [eventSearchFilter, setEventSearchFilter] = useLocalStorage<EventSearchState>(LocalStorageKeys.EventSearchFilter, {
    districtIds: undefined,
    searchString: '',
  });
  const [eventDateFilter, setEventDateFilter] = useLocalStorage<EventDateFilter>(LocalStorageKeys.EventDateFilterList, {
    startDate: undefined,
    endDate: undefined,
  });

  const formMethods = useForm({
    defaultValues: {
      districtIds: eventSearchFilter.districtIds || undefined,
      associationId: undefined,
    },
  });

  const { handleSubmit } = formMethods;

  const memoizedSearchFilter: EventSearchFilter = useMemo(() => {
    return {
      associationIds: eventSearchFilter.districtIds
        ? eventSearchFilter.districtIds
            .filter((x) => !x.isParent)
            .map((x) => x.code)
            .map(Number)
        : undefined,
      districtIds: eventSearchFilter.districtIds
        ? eventSearchFilter.districtIds
            .filter((x) => x.isParent)
            .map((x) => x.code)
            .map(Number)
        : undefined,
      searchString: eventSearchFilter?.searchString,
    };
  }, [eventSearchFilter.districtIds, eventSearchFilter?.searchString]);

  const memoizedDateFilter: EventDateFilter = useMemo(() => {
    return {
      startDate: eventDateFilter?.startDate,
      endDate: eventDateFilter?.endDate,
    };
  }, [eventDateFilter.endDate, eventDateFilter.startDate]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setEventSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setEventSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setEventSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setEventSearchFilter, tempSearchString]);

  // Hjälpfunktion för att uppdatera status (datum)
  const updateEventDateFilter = (startDate: string, endDate: string) => {
    setEventDateFilter(() => ({
      startDate,
      endDate,
    }));
  };

  const callbackStatuses = (val: EventStatusTabItem[]) => {
    // Hämta dagens och gårdagens datum
    const today = dayjs().format('YYYY-MM-DD');
    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

    // Mappa varje nyckel till dess logik för start- och slutdatum
    const tabLogic: { [key: string]: () => { startDate: string; endDate: string } } = {
      [EventStatusTabKeysEnum.Upcoming]: () => ({ startDate: today, endDate: '' }),
      [EventStatusTabKeysEnum.Completed]: () => ({ startDate: '', endDate: yesterday }),
      // Lägg till fler logiker för andra tabbar här
    };

    // Kontrollera om både Upcoming och Completed är valda
    const hasUpcoming = val.some((item) => item.key === EventStatusTabKeysEnum.Upcoming);
    const hasCompleted = val.some((item) => item.key === EventStatusTabKeysEnum.Completed);

    // Om både Upcoming och Completed är valda, sätt startDate och endDate till ''
    if (hasUpcoming && hasCompleted) {
      updateEventDateFilter('', '');
      return;
    }

    // Sätt standardvärden för start- och slutdatum
    let startDate = '';
    let endDate = '';

    // Applicera logik för varje vald tab
    val.forEach((item) => {
      if (tabLogic[item.key]) {
        const result = tabLogic[item.key]();
        startDate = startDate || result.startDate;
        endDate = endDate || result.endDate;
      }
    });

    // Uppdatera event search filter
    updateEventDateFilter(startDate, endDate);
  };

  const memoizedStatuses: EventStatusTabKeysEnum[] = useMemo(() => {
    const statuses: EventStatusTabKeysEnum[] = [];

    if (eventDateFilter.startDate && eventDateFilter.startDate.trim() !== '') {
      statuses.push(EventStatusTabKeysEnum.Upcoming);
    }

    if (eventDateFilter.endDate && eventDateFilter.endDate.trim() !== '') {
      statuses.push(EventStatusTabKeysEnum.Completed);
    }

    return statuses;
  }, [eventDateFilter.endDate, eventDateFilter.startDate]);

  const handleDistrict = useCallback(
    (x: District[] | undefined) => {
      if (x && x.length === 0) {
        setEventSearchFilter((prev) => ({
          ...prev,
          districtIds: undefined,
        }));
        return;
      }

      setEventSearchFilter((prev) => ({
        ...prev,
        districtIds: x,
      }));
    },
    [setEventSearchFilter],
  );

  const onSubmit = (data, e) => {
    e.preventDefault();
    ConsoleHelper.log('data', data);
    setEventSearchFilter(data);
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={eventSearchFilter.searchString || ''}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
            centerContent={<EventOverviewStatuses initialValue={memoizedStatuses} callback={callbackStatuses} />}
            rightContent={currentUser ? <ControlledMultiDistrictPicker name='districtIds' callback={(x) => handleDistrict(x)} /> : null}
          />
        </form>
      </FormProvider>
      <EventsDataGrid searchFilter={memoizedSearchFilter} dateFilter={memoizedDateFilter} />
    </>
  );
};
